// src/components/CompartirTallerModal.tsx
//
// Modal reutilizable para compartir el link público de un evento/taller.
// Genera una URL hacia /ins-taller/:id (ruta accesible al público sin sesión)
// y ofrece: copiar al portapapeles, QR descargable, y botones de redes sociales.

import React, { useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Copy,
  Check,
  Download,
  X as XIcon,
  MessageCircle,
  Facebook,
  Send,
  Mail,
  Twitter
} from 'lucide-react';

interface Props {
  visible: boolean;
  onClose: () => void;
  tallerId: string;
  nombre: string;
  fechaTexto?: string;
  estado?: string;
}

const buildPublicUrl = (tallerId: string): string => {
  // Respeta el basename del router (BrowserRouter basename="/modulob")
  const basename = '/modulob';
  const origin = window.location.origin;
  return `${origin}${basename}/ins-taller/${tallerId}`;
};

const buildShareText = (nombre: string, fechaTexto?: string): string => {
  const lineas = [`📅 ${nombre} — AMPI Riviera Nayarit`];
  if (fechaTexto) lineas.push(`🗓️ ${fechaTexto}`);
  lineas.push('Inscríbete aquí:');
  return lineas.join('\n');
};

export default function CompartirTallerModal({
  visible,
  onClose,
  tallerId,
  nombre,
  fechaTexto,
  estado
}: Props) {
  const [copiado, setCopiado] = useState(false);
  const qrRef = useRef<HTMLDivElement | null>(null);

  if (!visible) return null;

  const url = buildPublicUrl(tallerId);
  const texto = buildShareText(nombre, fechaTexto);
  const textoConUrl = `${texto} ${url}`;

  const copiarUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Fallback para navegadores sin Clipboard API (HTTP, navegadores viejos)
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
  };

  const descargarQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `qr-evento-${tallerId}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const enc = encodeURIComponent;
  const shareLinks = {
    whatsapp: `https://wa.me/?text=${enc(textoConUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    twitter: `https://twitter.com/intent/tweet?text=${enc(texto)}&url=${enc(url)}`,
    telegram: `https://t.me/share/url?url=${enc(url)}&text=${enc(texto)}`,
    email: `mailto:?subject=${enc(`${nombre} — AMPI Riviera Nayarit`)}&body=${enc(textoConUrl)}`
  };

  const advertenciaEstado = estado === 'finalizado' || estado === 'cancelado';

  return (
    <div
      className="modal d-block"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="compartir-modal-titulo"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="compartir-modal-titulo">
              <i className="fa-solid fa-share-nodes me-2 text-primary"></i>
              Compartir evento
            </h5>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            {advertenciaEstado && (
              <div className="alert alert-warning small mb-3">
                <i className="fa-solid fa-triangle-exclamation me-1"></i>
                Este evento está marcado como <strong>{estado}</strong>. El link sigue funcionando, pero los visitantes no podrán inscribirse.
              </div>
            )}

            <div className="mb-3">
              <h6 className="mb-1">{nombre}</h6>
              {fechaTexto && <small className="text-muted">{fechaTexto}</small>}
            </div>

            {/* URL + copiar */}
            <label className="form-label small fw-bold">Enlace público</label>
            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control"
                value={url}
                readOnly
                onClick={(e) => (e.target as HTMLInputElement).select()}
                aria-label="URL del evento"
              />
              <button
                className={`btn ${copiado ? 'btn-success' : 'btn-primary'}`}
                onClick={copiarUrl}
                type="button"
              >
                {copiado ? (
                  <><Check size={16} className="me-1" /> Copiado</>
                ) : (
                  <><Copy size={16} className="me-1" /> Copiar</>
                )}
              </button>
            </div>

            <div className="row g-3">
              {/* QR */}
              <div className="col-md-5 text-center">
                <label className="form-label small fw-bold d-block">Código QR</label>
                <div ref={qrRef} className="d-inline-block p-3 bg-white border rounded">
                  <QRCodeCanvas value={url} size={160} level="M" includeMargin={false} />
                </div>
                <button className="btn btn-outline-secondary btn-sm mt-2 w-100" onClick={descargarQR} type="button">
                  <Download size={14} className="me-1" />
                  Descargar QR (PNG)
                </button>
              </div>

              {/* Redes sociales */}
              <div className="col-md-7">
                <label className="form-label small fw-bold">Compartir en</label>
                <div className="d-grid gap-2">
                  <a
                    href={shareLinks.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-success d-flex align-items-center justify-content-start"
                  >
                    <MessageCircle size={18} className="me-2" />
                    WhatsApp
                  </a>
                  <a
                    href={shareLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn d-flex align-items-center justify-content-start text-white"
                    style={{ background: '#1877f2' }}
                  >
                    <Facebook size={18} className="me-2" />
                    Facebook
                  </a>
                  <a
                    href={shareLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-dark d-flex align-items-center justify-content-start"
                  >
                    <Twitter size={18} className="me-2" />
                    X (Twitter)
                  </a>
                  <a
                    href={shareLinks.telegram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn d-flex align-items-center justify-content-start text-white"
                    style={{ background: '#0088cc' }}
                  >
                    <Send size={18} className="me-2" />
                    Telegram
                  </a>
                  <a
                    href={shareLinks.email}
                    className="btn btn-outline-secondary d-flex align-items-center justify-content-start"
                  >
                    <Mail size={18} className="me-2" />
                    Correo electrónico
                  </a>
                </div>
              </div>
            </div>

            {/* Texto sugerido para copiar manualmente */}
            <div className="mt-4">
              <label className="form-label small fw-bold">Texto sugerido (para Instagram, stories, etc.)</label>
              <textarea
                className="form-control form-control-sm"
                rows={3}
                value={textoConUrl}
                readOnly
                onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                aria-label="Texto promocional sugerido"
              />
              <small className="text-muted">
                Click para seleccionar todo el texto.
              </small>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              <XIcon size={16} className="me-1" />
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
